import { datadogLogs } from "@datadog/browser-logs";
import {
  REDO_METADATA_ATTRIBUTE_KEY,
  REDO_OPTED_IN_FROM_CART_ATTRIBUTE_KEY,
  RedoMetadata,
} from "@redotech/redo-model/storefront/cart-attributes";
import { AttachmentStrategy } from "@redotech/redo-model/team";
import {
  defaultTermsAndConditionsBlockedMessage,
  WidgetAutocheck,
} from "@redotech/redo-model/widget";
import {
  ExtensionShopifyClient,
  ShopifyCartItem,
} from "@redotech/shopify-client/ajax";
import { StorefrontClient } from "../../client/storefront";
import { STANDARD_CHECKOUT_BUTTON } from "../../selectors";
import { isRedoItem } from "../../utils";
import { safeCartAdd, safeUpdateCartAttribute } from "../cart";
import { shouldBlockCheckout } from "../terms-and-conditions";

type ShopPayEventParams = {
  storefrontClient: StorefrontClient;
  productVariantId: string;
  productVariantQuantity: number;
  redoProductVariantId: string;
};

type CheckoutButtonItemsToAdd = { items: Partial<ShopifyCartItem>[] };

const FALLBACK_CHECKOUT_TIMEOUT_MS = 6500;

/**
 * Creates an event handler for Shop Pay button clicks on product pages.
 * When triggered, it creates a cart with both the selected product and the Redo offering,
 * then redirects to Shop Pay checkout.
 */
export function getShopPayProductOnClickEvent({
  storefrontClient,
  productVariantId,
  productVariantQuantity,
  redoProductVariantId,
}: ShopPayEventParams) {
  return async () => {
    try {
      const lines = [
        {
          merchandiseId: productVariantId,
          quantity: productVariantQuantity,
          attributes: [],
        },
        { merchandiseId: redoProductVariantId, quantity: 1, attributes: [] },
      ];

      const metadata: RedoMetadata = { optedTimestamp: Date.now() };

      const cart = await storefrontClient.createCart({
        lines,
        attributes: [
          { key: REDO_OPTED_IN_FROM_CART_ATTRIBUTE_KEY, value: "true" },
          { key: REDO_METADATA_ATTRIBUTE_KEY, value: JSON.stringify(metadata) },
        ],
        discountCodes: [],
      });

      if (cart?.checkoutUrl) {
        location.href = `${cart.checkoutUrl}&payment=shop_pay&redirect_source=direct_checkout_product`;
      } else {
        redirectToStandardCheckout();
      }
    } catch (error) {
      console.error("Error creating cart for Shop Pay checkout:", error);
      redirectToStandardCheckout();
    }
  };
}

/**
 * Fallback redirect to standard checkout
 */
function redirectToStandardCheckout() {
  location.href = `${location.origin}/checkout`;
}

export type onClickProps = {
  autocheck: WidgetAutocheck;
  client: ExtensionShopifyClient;
  itemsToAdd: CheckoutButtonItemsToAdd;
  cartParams: any;
  coverageCheckoutButtonClicked: boolean;
  setCoverageCheckoutButtonClicked: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  allowingBubblingRef: React.MutableRefObject<boolean>;
  triggerCartRefresh: () => void;
};

export async function checkAndRemoveRedoFromCart(
  client: ExtensionShopifyClient,
) {
  const cart = await client.cartGet();
  for (const item of cart.items) {
    if (isRedoItem(item)) {
      await client.removeItemFromCart(item, item.quantity);
    }
  }
}

export function getCheckoutOnClickEvent(
  addCoverage: boolean,
  isBuyNowHandler: boolean,
  {
    autocheck,
    client,
    itemsToAdd,
    cartParams,
    coverageCheckoutButtonClicked,
    setCoverageCheckoutButtonClicked,
    allowingBubblingRef,
    triggerCartRefresh,
  }: onClickProps,
) {
  return async (event: React.MouseEvent | React.TouchEvent) => {
    if (allowingBubblingRef.current) {
      return;
    } else {
      // prevent default logic if we're inside a form
      if (isBuyNowHandler) event.stopPropagation();
      event.preventDefault();
    }

    // handle Terms and condition
    if (
      autocheck.termsAndConditions?.blockCheckoutIfNotAccepted &&
      autocheck.termsAndConditions.termsCheckboxSelector &&
      shouldBlockCheckout(autocheck.termsAndConditions.termsCheckboxSelector)
    ) {
      alert(
        autocheck.termsAndConditions.blockedMessageText ||
          defaultTermsAndConditionsBlockedMessage,
      );
      return;
    }

    const metadata: RedoMetadata = { optedTimestamp: Date.now() };

    // set opted in cart attribute
    await safeUpdateCartAttribute({
      client,
      attributes: {
        attributes: [
          {
            key: REDO_OPTED_IN_FROM_CART_ATTRIBUTE_KEY,
            value: addCoverage ? "true" : "false",
          },
          { key: REDO_METADATA_ATTRIBUTE_KEY, value: JSON.stringify(metadata) },
        ],
      },
      retries: 1,
    });

    const shouldAddProduct =
      autocheck?.attachmentStrategy ===
        AttachmentStrategy.CHECKOUT_BUTTON_PRODUCT &&
      addCoverage &&
      autocheck.addProductOnOptInClick !== false;

    // if the coverage button was already clicked don't add the redo product again.
    if (shouldAddProduct && !coverageCheckoutButtonClicked) {
      // this blocks ensureProduct from removing the redo variant we're adding
      setCoverageCheckoutButtonClicked(true);
      await safeCartAdd({
        client,
        items: itemsToAdd,
        params: cartParams,
        retries: 2,
      });
    }

    // if someone clicks the non-coverage button and somehow had also clicked
    // the coverage button. set coverage button clicked to false so the redo product
    // will be removed
    if (!addCoverage) {
      setCoverageCheckoutButtonClicked(false);

      if (globalThis.redoStorefront?.type === "commercecloud") {
        await checkAndRemoveRedoFromCart(client);
      }
    }

    if (isBuyNowHandler) {
      const currTarget = event.target as HTMLElement;
      allowingBubblingRef.current = true;
      if ((currTarget as any)?.button) {
        (currTarget as any)?.button?.dispatchEvent(new Event("click"));
      } else if (currTarget?.click) {
        currTarget?.click();
      } else {
        currTarget?.dispatchEvent(new Event("click"));
      }
      return;
    }

    // click merchants standard checkout button
    if (autocheck.clickStandardCheckoutButton?.enabled) {
      const selector =
        autocheck.clickStandardCheckoutButton.checkoutButtonSelector ||
        STANDARD_CHECKOUT_BUTTON;
      const checkoutButton = document.querySelector(selector) as
        | HTMLButtonElement
        | undefined;

      if (checkoutButton?.click) {
        checkoutButton.click();

        if (globalThis.redoStorefront?.type === "commercecloud") {
          if (typeof globalThis.redoBasketUpdate === "function") {
            globalThis.redoBasketUpdate(); // Custom function City Beach gave us to update the cart. Does a GET https://dev.citybeach.com/on/demandware.store/Sites-CityBeachAustralia-Site/en_AU/Redo-Update and then fires page events to refresh cart numbers.
            triggerCartRefresh();
          } else {
            window.location.reload();
          }
        }

        if (autocheck.autoNavigateToCheckoutAfterTimeout ?? true) {
          return await goToCheckoutAfterTimeout();
        }
        return;
      }
    }

    if (globalThis.redoStorefront?.type === "commercecloud") {
      // The only SFCC merchant we have uses checkout buttons on the checkout page & clicks the old merchant button. If the next SFCC merchant wants it pre-checkout, make a setting.
      // Just double check the checkout URLs are correct for the new SFCC merchant and maybe removed this. Or, create a setting for checkout URL.
      throw new Error(
        "Merchant checkout buttons were not clicked, and no alternate checkout functionality has been tested",
      );
    }

    // Bundler app integration
    if (isSiteUsingBundlerApp()) {
      try {
        (globalThis.bndlr as Bundler).goToCheckout();
        return await goToCheckoutAfterTimeout();
      } catch (error) {
        console.warn("Error calling bndlr.goToCheckout:", error);
      }
    }

    // send shopper directly to checkout
    goToCheckout();
  };
}

const isSiteUsingBundlerApp = (): boolean => {
  return (
    typeof globalThis.bndlr !== "undefined" && !!globalThis.bndlr?.goToCheckout
  );
};

const goToCheckoutAfterTimeout = async () => {
  await new Promise((resolve) =>
    setTimeout(resolve, FALLBACK_CHECKOUT_TIMEOUT_MS),
  );
  datadogLogs.logger.info(
    "Checkout buttons is too slow or not working. sending shopper to checkout",
  );
  goToCheckout();
};

export const goToCheckout = () => {
  location.href = location.origin + "/checkout";
};

export const getCheckoutButtonItemsToAdd = ({
  singleProductVariantId,
  hasFinalSaleCoverage,
  sellingPlanId,
}: {
  singleProductVariantId: string | number;
  hasFinalSaleCoverage: boolean;
  sellingPlanId: number | null;
}): CheckoutButtonItemsToAdd => {
  return {
    items: [
      {
        id: singleProductVariantId,
        quantity: 1,
        properties: hasFinalSaleCoverage
          ? { __final_sale_coverage: "true" }
          : {},
        selling_plan: sellingPlanId ?? undefined,
      },
    ],
  };
};
